import { defineStore } from 'pinia';
import depositWithdraw from 'service/depositWithdraw';
import {
    DepositChannelModel,
    Payments,
    PromotionModel,
    PreferentialRuleModel,
    GetMoneyDiscountRuleType,
    WithdrawSysObjModel,
    MyDigitalListModel,
    MyNonDigitalListModel,
    nonDigitalListType,
    MaterialModel,
    BankOptionModel,
    DigitalOptionModel,
    BankTypeModel,
    preferentialActivityObjType,
    withdrawProgressListType,
    currentDiscountObjType
} from 'types/depositWithdraw';
import send from 'service/index';

interface DepositWithdrawState {
    // 1. 存款相关
    channelAllList: DepositChannelModel[];
    currentChannelId: number | null;
    currentChannelPayMethodId: number | null;
    currentChannel: DepositChannelModel | null;
    currentPaymentList: Payments[];
    currentPaymentObj: Payments | null;
    depositMoney: string;
    depositMoney2: string;
    openMoneyList: string[];
    moneyWithRuleList: string[];
    isSelectPayment: number;
    minAmount: number;
    maxAmount: number;
    realName: string;

    // 2. 存款优惠相关
    extraBonusAllList: PromotionModel[] | null;
    isFirst: boolean;
    preferentialActivityId: number;
    preferentialActivityObj: preferentialActivityObjType;
    rulesList: PreferentialRuleModel[] | null;
    currentDiscountPercent: string;
    currentDiscountPercentValue: string;

    // 3. 提款相关
    withdrawSysObj: WithdrawSysObjModel | null;
    withdrawSysObjV2: WithdrawSysObjModel | null;
    activeWithdrawIndex: string;
    activeWithdrawIndex2: string;
    myDigitalList: MyDigitalListModel[] | null; // 已绑数字货币列表
    myBankList: MyNonDigitalListModel[] | null; // 已绑银行卡列表
    myPixList: MyNonDigitalListModel[] | null; // 已绑PIX列表
    myThreeList: MyNonDigitalListModel[] | null; // 已绑三方钱包列表
    myAlipayList: MyNonDigitalListModel[] | null; // 已绑银行卡列表
    nonDigitalList: nonDigitalListType; // 非USDT列表

    // 4. 绑卡&完善信息相关
    needToCompleteInput: MaterialModel | null;
    hasCompleteInput: MaterialModel | null;
    withdrawIsComplete: boolean;
    bankOptionList: BankOptionModel[] | null;
    digitalOptionList: DigitalOptionModel[] | null;
    bankTypeList: BankTypeModel[] | null;
    pixOptionList: BankTypeModel[] | null;
    profitTypeList: BankTypeModel[] | null;
    withdrawProgressList: withdrawProgressListType[] | null;
    isAutoSelectedChannel: boolean;
    currentDiscountObj: currentDiscountObjType;
    checkList: number[];
}

function sortPaymentsByPriority(payments: Payments[]): Payments[] {
    return [...payments].sort((a, b) => {
        const sortDiff = (a.sort ?? 0) - (b.sort ?? 0);
        if (sortDiff !== 0) return sortDiff;

        const payOkRate1Diff = (b.payOkRate1 ?? 0) - (a.payOkRate1 ?? 0);
        if (payOkRate1Diff !== 0) return payOkRate1Diff;

        return (b.payOkRate ?? 0) - (a.payOkRate ?? 0);
    });
}

export const useDepositWithdrawStore = defineStore({
    id: 'depositWithdraw',
    state: (): DepositWithdrawState => ({
        // 1. 存款相关
        channelAllList: [],
        currentChannelId: null,
        currentChannelPayMethodId: null,
        currentChannel: null,
        currentPaymentList: [],
        currentPaymentObj: null,
        depositMoney: '',
        depositMoney2: '',
        openMoneyList: [],
        moneyWithRuleList: ['110', '300', '500', '1000', '2000', '5000', '10000', '20000', '35000', '50000', '80000', '100000'],
        isSelectPayment: -9527,
        minAmount: 0,
        maxAmount: 0,
        realName: '',

        // 2. 存款优惠相关
        extraBonusAllList: null,
        isFirst: false,
        preferentialActivityId: -1,
        preferentialActivityObj: {},
        rulesList: null,
        currentDiscountPercent: '~%',
        currentDiscountPercentValue: '',

        // 3. 提款相关
        withdrawSysObj: null,
        withdrawSysObjV2: null,
        activeWithdrawIndex: '',
        activeWithdrawIndex2: '',
        myDigitalList: null,
        myBankList: null,
        myPixList: null,
        myThreeList: null,
        myAlipayList: null,
        nonDigitalList: {
            pix: 'findPixList',
            bank: 'findBankList',
            three: 'findThirdPartyWalletList',
            alipay: 'findAliPayList',
        },

        // 4. 绑卡&完善信息相关
        needToCompleteInput: null,
        hasCompleteInput: null,
        withdrawIsComplete: false,
        bankOptionList: null,
        digitalOptionList: null,
        bankTypeList: null,
        pixOptionList: null,
        profitTypeList: null,
        withdrawProgressList: [],
        isAutoSelectedChannel: false,
        currentDiscountObj: {
            content: '',
            finishCount: -1,
            icon: '',
            id: -1,
            maxCount: -1,
            maxPreferentialAmount: -1,
            name: '',
            nowFinishCount: -1,
            status: -1,
            isCheck: false,
        },
        checkList: [],
    }),

    actions: {
        // 清空存款信息
        clearDeposit() {
            this.currentPaymentObj = {} as Payments;
            this.depositMoney = '';
            this.rulesList = [];
            this.currentDiscountPercent = '~%';
            this.currentDiscountPercentValue = '';
            this.isFirst = false;
            this.preferentialActivityId = -1;
        },



        // 清空支付方式
        clearMethodDeposit() {
            this.depositMoney = '';
            this.currentDiscountPercent = '~%';
            this.currentDiscountPercentValue = '';
        },

        // 设置真实姓名
        setRealName(value: string) {
            this.realName = value;
        },

        // 设置渠道列表
        setChannelAllList(value: DepositChannelModel[]) {
            this.channelAllList = value;
        },

        // 设置当前渠道
        setCurrentChannelId(value: DepositChannelModel | null) {
            if (!value) return;
            this.currentChannelId = value.channelTypeId ?? null;
            this.currentChannelPayMethodId = value.payMethodId ?? null;
            this.currentChannel = value;
            this.openMoneyList = value.openMoney?.split(',') ?? [];
            this.minAmount = value.minAmount ?? 0;
            this.maxAmount = value.maxAmount ?? 0;
            this.depositMoney = '';
            this.isAutoSelectedChannel = value.isAutoSelectedChannel || false;

            console.log('isAutoSelectedChannel:', value);

        },

        // 设置当前支付方式列表
        setCurrentPayments(value: Payments[] | null) {
            console.log('存款方式点击');
            this.currentPaymentList = value ?? [];
        },

        // 设置当前优惠活动对象
        setCurrentDiscountObj(value: currentDiscountObjType) {
            this.currentDiscountObj = value;
        },

        // 设置选中优惠活动ID
        setCheckList(value: number[]) {
            this.checkList = value;
        },

        // 设置当前支付方式
        setCurrentPaymentObj(value: Payments | null) {
            console.log('paymentId:', value?.paymentId);
            if (value?.paymentId != null) {
                // 如果选择了支付方式,默认选择openMoney第一个金额
                this.currentPaymentObj = value;
                this.isSelectPayment = value.paymentId;

                // 如果金额不在maxAmount ~ minAmount范围内,直接清空金额
                const currentAmount = parseFloat(this.depositMoney) || 0;
                if ((value.maxAmount ?? 0) < currentAmount || (value.minAmount ?? 0) > currentAmount) {
                    this.depositMoney = '';
                    this.currentDiscountPercent = '~%';
                    this.currentDiscountPercentValue = '';
                }
            } else {
                // 如果没有选择支付方式,使用最外层的openMoney
                this.currentPaymentObj = {} as Payments;
                this.isSelectPayment = -9527;
                this.depositMoney = '';
            }
        },

        // 设置存款金额
        setDepositMoney(value: string, value2?: string) {
            console.log('点击存款金额:', value, 'isSelectPayment:', this.isSelectPayment);
            this.depositMoney = value;
            this.depositMoney2 = value2 || value;

            // isSelectPayment != -9527 表示手动选择了支付方式
            if (this.isSelectPayment !== -9527) {
                const newArr = this.currentPaymentList.filter(item => this.isSelectPayment === item.paymentId);

                if (newArr.length > 0) {
                    // 选择了支付方式, 并且金额在范围内, 则直接使用选择的支付方式
                    const currentAmount = parseFloat(value) || 0;
                    if ((newArr[0].maxAmount ?? 0) >= currentAmount && (newArr[0].minAmount ?? 0) <= currentAmount) {
                        this.currentPaymentObj = newArr[0];
                        return;
                    }
                }
            }

            // 如果没有选择支付方式, 按 sort -> payOkRate1 -> payOkRate 排序选择
            const currentAmount = parseFloat(value) || 0;
            const newArr = this.currentPaymentList.filter(item => (item.maxAmount ?? 0) >= currentAmount && (item.minAmount ?? 0) <= currentAmount);

            if (newArr.length > 0) {
                this.currentPaymentObj = sortPaymentsByPriority(newArr)[0];
            } else {
                this.currentPaymentObj = {} as Payments;
            }
        },
        // 设置存款金额
        setDepositMoney2(value: string, value2?: string) {
            console.log('点击存款金额:', value, 'isSelectPayment:', this.isSelectPayment);
            this.depositMoney = value;
            this.depositMoney2 = value2 || value;

            // isSelectPayment != -9527 表示手动选择了支付方式
            if (this.isSelectPayment !== -9527) {
                const newArr = this.currentPaymentList.filter(item => this.isSelectPayment === item.paymentId);

                if (newArr.length > 0) {
                    // 选择了支付方式, 并且金额在范围内, 则直接使用选择的支付方式
                    const currentAmount = parseFloat(value) || 0;
                    if ((newArr[0].maxAmount ?? 0) >= currentAmount && (newArr[0].minAmount ?? 0) <= currentAmount && this.isAutoSelectedChannel) {
                        this.currentPaymentObj = newArr[0];
                        return;
                    }
                }
            }

            // 如果没有选择支付方式, 按 sort -> payOkRate1 -> payOkRate 排序选择
            const currentAmount = parseFloat(value) || 0;
            const newArr = this.currentPaymentList.filter(item => (item.maxAmount ?? 0) >= currentAmount && (item.minAmount ?? 0) <= currentAmount);

            if (newArr.length > 0 && this.isAutoSelectedChannel) {
                this.currentPaymentObj = sortPaymentsByPriority(newArr)[0];
            } else {
                this.currentPaymentObj = {} as Payments;
            }
        },

        // 获取存款渠道列表
        async getApiChannelAllList() {
            try {
                const response = await depositWithdraw.getDepositList({});

                if (response?.data) {
                    const channelAllList = response.data;
                    this.setChannelAllList(channelAllList);
                    if (channelAllList.length > 0) {
                        this.setCurrentChannelId(channelAllList[0]);
                        this.currentPaymentList = channelAllList[0].payments ?? [];
                        this.openMoneyList = channelAllList[0].openMoney?.split(',') ?? [];
                    }
                }

                return response;
            } catch (e) {
                console.error('获取存款渠道列表错误:', e);
            }
        },

        // 存款提交
        async depositSubmit(data: any) {
            try {
                return await depositWithdraw.depositSubmit(data);
            } catch (e) {
                console.error('存款提交失败:', e);
                return Promise.reject(e);
            }
        },

        // 设置存款优惠列表
        setExtraBonusAllList(value: PromotionModel[]) {
            this.extraBonusAllList = value;
        },

        // 设置是否首次
        setIsFirst(value: boolean) {
            this.isFirst = value;
        },

        // 设置优惠活动ID
        setPreferentialActivityId(value: number) {
            this.preferentialActivityId = value;
        },

        // 获取优惠活动数据
        setPreferentialActivityObj(value: preferentialActivityObjType) {
            this.preferentialActivityObj = value;
        },

        // 设置规则列表
        setRulesList(value: PreferentialRuleModel[]) {
            this.rulesList = value;
        },

        // 设置当前优惠百分比
        setCurrentDiscountPercent(getValue: GetMoneyDiscountRuleType) {
            const value = getValue.getMoney || '';
            const resultValue = getValue.resultType === 2 ? getValue.resultValue || '' : '';
            this.currentDiscountPercent = value || '~%';
            this.currentDiscountPercentValue = resultValue || '';
        },

        async processSelectedObject(selectObj: PromotionModel) {
            console.log('selectObj:', selectObj);
            if (selectObj.id === this.preferentialActivityId) {
                return;
            }
            this.setIsFirst(selectObj.id !== -1);
            this.setCurrentDiscountObj(selectObj);
            this.setCheckList([selectObj.id]);
            this.setPreferentialActivityId(selectObj.id);
            this.setPreferentialActivityObj(selectObj); // 添加这行，确保 preferentialActivityObj 也被设置
            await this.getApiExtraRuleList(selectObj.id);

            const rulesList = this.rulesList?.map(e => ({ ...e })) ?? [];

            // 注意：这里需要实现getMoneyDiscountRule方法
            const discountRule = this.getMoneyDiscountRule(
                this.depositMoney || '0',
                rulesList,
                'INR' // 从配置中获取货币类型
            );

            this.setCurrentDiscountPercent(discountRule);
        },

        // 获取存款优惠列表
        async getApiExtraBonusList(activityId?: string) {
            try {
                const result = await depositWithdraw.getDepositDiscountList({});
                if (!result?.data) return;

                const channelAllList = result.data;
                this.setExtraBonusAllList(channelAllList);
                let newChannelAllList = channelAllList.filter(item => (item.payMethodId === this.currentChannelPayMethodId || item.payMethodId === -1));
                console.log('newChannelAllList:', newChannelAllList);

                const index = newChannelAllList.findIndex(item => item.isCheck);
                const getActivityId = parseInt(activityId || '0') || 0;
                const selectIndex = newChannelAllList.findIndex(item => item.id === getActivityId);

                console.log('index:', index, selectIndex, 'activityId:', activityId);

                if (getActivityId > 0 && selectIndex !== -1) {
                    console.log('selectIndex:', selectIndex);
                    await this.processSelectedObject(newChannelAllList[selectIndex]);
                } else if (index !== -1) {
                    console.log('index:', index);
                    await this.processSelectedObject(newChannelAllList[index]);
                }
            } catch (e) {
                console.error('获取存款优惠列表错误:', e);
            }
        },


        // 获取存款优惠列表
        async getApiExtraBonusList2(activityId?: string) {
            try {
                const channelAllList = this.extraBonusAllList || [];
                let newChannelAllList = channelAllList?.filter(item => (item.payMethodId === this.currentChannelPayMethodId || item.payMethodId === -1));
                console.log('newChannelAllList:', newChannelAllList);

                const index = newChannelAllList?.findIndex(item => item.isCheck);
                const getActivityId = parseInt(activityId || '0') || 0;
                const selectIndex = newChannelAllList?.findIndex(item => item.id === getActivityId);

                console.log('index:', index, selectIndex, 'activityId:', activityId);

                if (getActivityId > 0 && selectIndex !== -1) {
                    console.log('selectIndex:', selectIndex);
                    await this.processSelectedObject(newChannelAllList?.[selectIndex]);
                } else if (index !== -1) {
                    console.log('index:', index);
                    await this.processSelectedObject(newChannelAllList?.[index]);
                } else {
                    this.setRulesList([]);
                    this.setCurrentDiscountPercent({ getMoney: '', resultValue: '', resultType: 0 });
                    this.setCurrentDiscountObj({
                        content: '',
                        finishCount: -1,
                        icon: '',
                        id: -1,
                        maxCount: -1,
                        maxPreferentialAmount: -1,
                        name: '',
                        nowFinishCount: -1,
                        status: -1,
                        isCheck: false,
                    });
                    this.setCheckList([]);
                }
            } catch (e) {
                console.error('获取存款优惠列表错误:', e);
            }
        },

        // 获取优惠规则列表
        async getApiExtraRuleList(preferentialId: number) {
            try {
                const result = await depositWithdraw.getDepositDiscountRuleList(preferentialId);
                if (!result?.data) return;

                const rulesList = result.data;
                this.setRulesList(rulesList);
            } catch (e) {
                console.error('获取优惠规则列表错误:', e);
            }
        },

        // 获取优惠规则计算结果
        getMoneyDiscountRule(newValue: string, ruleArr: any[], currency: string): GetMoneyDiscountRuleType {
            const value = parseFloat(newValue || '0');
            let getMoney = '';
            let resultValue = '';
            let resultType = 0;

            if (ruleArr?.length > 0) {
                for (const item of ruleArr) {
                    resultValue = item?.resultValue ?? '';
                    resultType = item?.resultType ?? 0;

                    const getMoneyValue = item.resultType === 2 ? item.resultLabel : `${parseInt(item.resultLabel || '0')}`;

                    switch (item.conditionCalculationMethod) {
                        case 1: // 等于
                            if (value === item.conditionResultValue) {
                                getMoney = getMoneyValue;
                                return { getMoney, resultValue, resultType };
                            }
                            break;
                        case 2: // 大于
                            if (value > item.conditionResultValue) {
                                getMoney = getMoneyValue;
                                return { getMoney, resultValue, resultType };
                            }
                            break;
                        case 3: // 大于等于
                            if (value >= item.conditionResultValue) {
                                getMoney = getMoneyValue;
                                return { getMoney, resultValue, resultType };
                            }
                            break;
                        case 4: // 小于
                            if (value < item.conditionResultValue) {
                                getMoney = getMoneyValue;
                                return { getMoney, resultValue, resultType };
                            }
                            break;
                        case 5: // 小于等于
                            if (value <= item.conditionResultValue) {
                                getMoney = getMoneyValue;
                                return { getMoney, resultValue, resultType };
                            }
                            break;
                        case 6: // 区间
                            if (value >= item.conditionResultValue && value < item.conditionMaxResultValue + 1) {
                                getMoney = getMoneyValue;
                                return { getMoney, resultValue, resultType };
                            }
                            break;
                    }
                }
            }

            return { getMoney, resultValue, resultType };
        },

        // 提款相关 actions
        setWithdrawSysObj(value: WithdrawSysObjModel | null) {
            this.withdrawSysObj = value;
            if (Array.isArray(value?.withdrawMethodList) && value.withdrawMethodList.length > 0) {
                this.setActiveWithdrawIndex(value?.withdrawMethodList[0]?.code || '');
            }
        },

        setWithdrawSysObjV2(value: WithdrawSysObjModel | null) {
            this.withdrawSysObjV2 = value;
            if (Array.isArray(value?.withdrawMethodList) && value.withdrawMethodList.length > 0) {
                console.log('setActiveWithdrawIndex2:', value?.withdrawMethodList[0]?.code);
                this.setActiveWithdrawIndex2(value?.withdrawMethodList?.[0]?.code || '');
            }
        },

        setActiveWithdrawIndex(value: string) {
            this.activeWithdrawIndex = value;
        },

        setActiveWithdrawIndex2(value: string) {
            this.activeWithdrawIndex2 = value;
        },

        setMyDigitalList(value: MyDigitalListModel[]) {
            this.myDigitalList = value;
        },

        setMyBankList(value: MyNonDigitalListModel[]) {
            this.myBankList = value;
        },

        setMyPixList(value: MyNonDigitalListModel[]) {
            this.myPixList = value;
        },

        setMyThreeList(value: MyNonDigitalListModel[]) {
            this.myThreeList = value;
        },

        setMyAlipayList(value: MyNonDigitalListModel[]) {
            this.myAlipayList = value;
        },

        setWithdrawalProgress(value: withdrawProgressListType[]) {
            this.withdrawProgressList = value;
        },

        // 获取提款系统对象
        async getApiWithdrawSysObj() {
            try {
                const result = await depositWithdraw.getWithdrawalObj({});
                if (result?.data) {
                    this.setWithdrawSysObj(result.data);
                }
                return result;
            } catch (e) {
                console.error('获取提款系统对象错误:', e);
            }
        },

        // 获取提款进度
        async getApiWithdrawalProgress(data: any) {
            try {
                const res = await depositWithdraw.getWithdrawalProgress(data);
                if (res?.code === 0) {
                    this.setWithdrawalProgress(res?.data || []);
                }
                return res;
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 获取提款系统对象V2
        async getApiWithdrawSysObjV2() {
            try {
                const result = await depositWithdraw.getFindBindCount({});
                if (result?.data) {
                    this.setWithdrawSysObjV2(result.data);
                }

                return result;
            } catch (e) {
                console.error('获取提款系统对象V2错误:', e);
            }
        },

        // 获取数字货币列表
        async getApiMyDigitalList() {
            try {
                const result = await depositWithdraw.getDigitalList({});
                if (result?.data) {
                    this.setMyDigitalList(result.data);
                }
            } catch (e) {
                console.error('获取数字货币列表错误:', e);
            }
        },

        // 获取非数字货币列表
        async getApiMyNonDigitalList(type: string) {
            try {
                const result = await depositWithdraw.getNonDigitalList(this.nonDigitalList[type]);

                const nonDigitalList = result?.data || [];

                // 根据类型设置不同的列表
                switch (type) {
                    case 'pix':
                        this.setMyPixList(nonDigitalList);
                        break;
                    case 'bank':
                        this.setMyBankList(nonDigitalList);
                        break;
                    case 'three':
                        this.setMyThreeList(nonDigitalList);
                        break;
                    case 'alipay':
                        this.setMyAlipayList(nonDigitalList);
                        break;
                }
            } catch (e) {
                console.error('获取非数字货币列表错误:', e);
            }
        },

        // 绑卡相关 actions
        setNeedToCompleteInput(value: MaterialModel | null) {
            this.needToCompleteInput = value;
        },

        setHasCompleteInput(value: MaterialModel | null) {
            this.hasCompleteInput = value;
        },

        setWithdrawIsComplete(value: boolean) {
            this.withdrawIsComplete = value;
        },

        setBankOptionList(value: BankOptionModel[]) {
            this.bankOptionList = value;
        },

        setDigitalOptionList(value: DigitalOptionModel[]) {
            this.digitalOptionList = value;
        },

        setBankTypeList(value: BankTypeModel[]) {
            this.bankTypeList = value;
        },

        setPixOptionList(value: BankTypeModel[]) {
            this.pixOptionList = value;
        },

        setProfitTypeList(value: BankTypeModel[]) {
            this.profitTypeList = value;
        },

        // 获取需要完善的字段
        async getApiNeedToComplete() {
            try {
                const result = await depositWithdraw.getNeedToComplete({});
                if (result?.data) {
                    this.setNeedToCompleteInput(result.data);
                }
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 获取已完善的字段
        async getApiHasCompleted() {
            try {
                const result = await depositWithdraw.getCompleteInfo({});
                if (result?.data) {
                    this.setHasCompleteInput(result.data);
                }
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 检查提款信息是否完整
        async getApiCheckWithdrawInfo() {
            try {
                const result = await depositWithdraw.checkWithdrawInfo({});
                if (result?.data?.code === 0) {
                    this.setWithdrawIsComplete(result.data.data);
                }

                return result;
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 获取银行卡选项
        async getApiBankOptions() {
            try {
                const result = await depositWithdraw.getBankOptions({});
                if (result?.data) {
                    this.setBankOptionList(result.data);
                }
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 获取银行卡类型列表
        async getApiBankTypeList() {
            try {
                const result = await depositWithdraw.getBankAccountOptions({
                    listAll: ['BankAccountTypeEnum'],
                });
                if (result?.data?.BankAccountTypeEnum) {
                    this.setBankTypeList(result.data.BankAccountTypeEnum);
                }
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 获取PIX类型选项
        async getApiPixOptions() {
            try {
                const result = await depositWithdraw.getPixOptions({});
                if (result?.data) {
                    this.setPixOptionList(result.data);
                }
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 获取数字钱包选项
        async getApiDigitalOptions() {
            try {
                const result = await depositWithdraw.getDigitalOptions({});
                if (result?.data) {
                    this.setDigitalOptionList(result.data);
                }
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 提交完整资料
        async submitUserInfo(data: any) {
            try {
                const result = await depositWithdraw.submitUserInfo(data);
                if (result?.data) {
                    this.setDigitalOptionList(result.data);
                }
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 检验提现密码
        async verifyPassword(data: any) {
            try {
                return await depositWithdraw.verifyPassword(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 提交绑卡, 支付宝
        async submitBindCard(data: any) {
            try {
                return await depositWithdraw.submitBindCard(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 提交绑卡, 支付宝 v2版
        async submitBindCardV2(data: any) {
            try {
                return await depositWithdraw.submitBindCardV2(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 提交绑PIX
        async submitBindPix(data: any) {
            try {
                return await depositWithdraw.submitBindPix(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 提交绑PIX(新, 全绑定)
        async submitBindPixs(data: any) {
            try {
                return await depositWithdraw.submitBindPixs(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 提交绑PIX(新, 全绑定)
        async submitBindDigital(data: any) {
            try {
                return await depositWithdraw.submitBindDigital(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // usdt提现
        async submitWithdrawDigital(data: any) {
            try {
                return await depositWithdraw.submitWithdrawDigital(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // (银行卡,支付宝, pix)提现
        async submitWithdraw(data: any) {
            try {
                return await depositWithdraw.submitWithdraw(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 补单
        async fillOrder(data: any) {
            try {
                return await depositWithdraw.fillOrder(data);
            } catch (e) {
                return Promise.reject(e);
            }
        },

        // 重置所有提款和绑卡相关数据
        resetAllWithdraw() {
            // 提款相关
            this.myDigitalList = null;
            this.bankOptionList = null;
            this.bankTypeList = null;
            this.myBankList = null;
            this.myPixList = null;
            this.myThreeList = null;
            this.myAlipayList = null;
            this.activeWithdrawIndex2 = '';
            this.withdrawSysObj = null;
            // 绑卡相关
            this.needToCompleteInput = null;
            this.hasCompleteInput = null;
        },

        // 获取利息宝类型列表
        async getProfitTypeList() {
            try {
                const result = await depositWithdraw.getBankAccountOptions({
                    listAll: ['InterestRecordTypeEnum'],
                });
                if (result?.data?.InterestRecordTypeEnum) {
                    this.setProfitTypeList(result.data.InterestRecordTypeEnum);
                    return result.data.InterestRecordTypeEnum;
                } else {
                    return []
                }

            } catch (e) {
                return Promise.reject(e);
            }
        },
    },
});
